// Der Seitenstapel der App-Ansicht.
//
// Jede Ebene bleibt eingehängt. Das ist der ganze Unterschied zwischen einer
// App und einer Website: wer von der Lagerliste in einen Artikel geht und
// zurückkommt, steht wieder an derselben Stelle der Liste, mit demselben
// Suchbegriff im Feld. Ein neu gerendertes <Routes> je Ebene könnte das
// nicht — deshalb merkt sich der Rahmen die Elemente, die useOutlet() zum
// jeweiligen Zeitpunkt geliefert hat, und React behält ihren Zustand, weil
// sie an derselben Stelle der Liste stehen bleiben.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate, useNavigationType, useOutlet } from 'react-router-dom';
import { naechsterStapel, eingedampft } from './stapel';
import type { Ort } from './stapel';
import { imStartbereich, loestAus, scrollerDavor, wegAus, RANDBREITE } from './geste';
import type { Bahnglied, Richtung } from './geste';
import { useSchmal } from '../gemeinsam/lib/schmal';
import { istNativ } from '../gemeinsam/lib/plattform';
import { tippen } from './haptik';
import { useSprache } from '../gemeinsam/lib/i18n';

/** Wie weit die darunterliegende Seite mitwandert. Weniger als die obere — das
 *  erzeugt den Eindruck von Tiefe, den jede native App benutzt. */
const TIEFE_PROZENT = 28;
const DAUER_MS = 380;
/** Überblendung ist kürzer als das Schieben — sie trägt keine Richtung. */
const DAUER_RUHIG_MS = 180;
const KURVE = 'cubic-bezier(.32,.72,0,1)';

type Ebene = {
  ort: Ort;
  inhalt: ReactNode;
  /**
   * Diese Ebene ist aus dem Stapel heraus, gleitet aber noch hinaus.
   *
   * Sie bleibt dafür EINGEHÄNGT. Würde sie sofort ausgehängt, verschwände die
   * Seite schlagartig statt zu gleiten — und beim Wischen genau an der Stelle,
   * wo der Finger losließ. Weil der Knoten erhalten bleibt, trägt er noch
   * seine Verschiebung aus der Geste und gleitet von dort weiter.
   */
  verlassend?: boolean;
};

/**
 * Leserichtung im Moment der Abfrage.
 *
 * Nicht beim Rendern festhalten: i18n setzt `document.dir` erst in einem
 * Effekt. Wer den Wert beim Rendern merkt, hat nach dem Umschalten auf
 * Arabisch noch die alte Richtung und den Gestenstreifen auf der falschen
 * Seite, bis zufällig neu gerendert wird.
 */
function richtungJetzt(): Richtung {
  return typeof document !== 'undefined' && document.dir === 'rtl' ? 'rtl' : 'ltr';
}

function ruhigGewuenscht(): boolean {
  return typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** Die Elternkette über einem Element, als Messwerte für die Gestenprüfung. */
function bahnVon(start: Element | null): Bahnglied[] {
  const bahn: Bahnglied[] = [];
  let el: Element | null = start;
  while (el && el !== document.body) {
    const s = getComputedStyle(el);
    bahn.push({
      scrollWidth: el.scrollWidth,
      clientWidth: el.clientWidth,
      scrollLeft: el.scrollLeft,
      ueberlaufX: s.overflowX,
    });
    el = el.parentElement;
  }
  return bahn;
}

export default function AppRahmen({ wurzelInhalt, rahmen }: {
  /** Was auf `/` erscheinen soll — in der App das Kachelbrett statt der Übersicht. */
  wurzelInhalt?: ReactNode;
  /**
   * Umschlag je Ebene: Seitenkopf und Scrollbereich. Liegt beim Aufrufer,
   * damit keine der 32 Seiten angefasst werden muss, um einen Kopf zu
   * bekommen — sie liefern weiter nur ihren Inhalt.
   */
  rahmen?: (inhalt: ReactNode, tiefe: number, pfad: string) => ReactNode;
}) {
  const ort = useLocation();
  const art = useNavigationType();
  const navigate = useNavigate();
  const outlet = useOutlet();
  const schmal = useSchmal();

  const inhaltFuer = useCallback(
    (pfad: string) => (pfad === '/' && wurzelInhalt ? wurzelInhalt : outlet),
    [outlet, wurzelInhalt],
  );

  const [ebenen, setEbenen] = useState<Ebene[]>(() => [
    { ort: { pfad: ort.pathname, schluessel: ort.key }, inhalt: inhaltFuer(ort.pathname) },
  ]);

  // Was sich beim letzten Mal geändert hat — daraus ergibt sich, ob und in
  // welche Richtung animiert wird.
  const bewegung = useRef<{ art: 'push' | 'pop'; schluessel: string } | null>(null);
  const laeuft = useRef(false);
  const flaechen = useRef(new Map<string, HTMLDivElement>());

  // --- Stapel fortschreiben -------------------------------------------------
  useEffect(() => {
    setEbenen(vorher => {
      // Verlassende Ebenen zählen nicht mehr zum Stapel — sie hängen nur noch
      // für ihre Ausblendung herum.
      const bestand = vorher.filter(e => !e.verlassend);
      const alt = bestand.map(e => e.ort);
      const neu = naechsterStapel(alt, { pfad: ort.pathname, schluessel: ort.key }, art);
      if (neu === alt) return vorher;

      bewegung.current =
        neu.length > alt.length ? { art: 'push', schluessel: ort.key }
          : neu.length < alt.length ? { art: 'pop', schluessel: ort.key }
            : null;

      // Vorhandene Ebenen behalten ihr Element — und damit ihren Zustand.
      const nachSchluessel = new Map(bestand.map(e => [e.ort.schluessel, e]));
      const bleibend = neu.map(o => nachSchluessel.get(o.schluessel)
        ?? { ort: o, inhalt: inhaltFuer(o.pfad) });

      if (bewegung.current?.art !== 'pop') return bleibend;

      // Nur die oberste verlassende Ebene gleitet sichtbar hinaus. Tiefere
      // lagen ohnehin verdeckt darunter und können sofort gehen.
      const hinaus = bestand[bestand.length - 1];
      const nochDa = hinaus && !neu.some(o => o.schluessel === hinaus.ort.schluessel);
      return nochDa ? [...bleibend, { ...hinaus, verlassend: true }] : bleibend;
    });
  }, [ort.key, ort.pathname, art, inhaltFuer]);

  // Eine hinausgeglittene Ebene endgültig aushängen.
  useEffect(() => {
    if (!ebenen.some(e => e.verlassend)) return;
    const weg = setTimeout(() => {
      setEbenen(v => (v.some(e => e.verlassend) ? v.filter(e => !e.verlassend) : v));
    }, DAUER_MS + 40);
    return () => clearTimeout(weg);
  }, [ebenen]);

  // --- Breitenwechsel -------------------------------------------------------
  useEffect(() => {
    // Wird es breit, gibt es keinen Stapel mehr. Nur eindampfen, nicht
    // navigieren: ein zusätzlicher Verlaufseintrag wäre hier ein Fehler.
    if (!schmal) setEbenen(v => (v.length <= 1 ? v : eingedampft(v.map(e => e.ort))
      .map(o => v.find(e => e.ort.schluessel === o.schluessel)!)));
  }, [schmal]);

  // --- Push / Pop animieren -------------------------------------------------
  useLayoutEffect(() => {
    const b = bewegung.current;
    bewegung.current = null;
    // Die Sperre wird IMMER wieder gelöst — auch wenn dieser Durchlauf nichts
    // zu animieren hat. Früher hing sie fest, sobald ein Tableistenwechsel
    // mitten in eine laufende Animation fiel, und die Wischgeste war von da an
    // still tot.
    const loesen = setTimeout(() => { laeuft.current = false; }, DAUER_MS + 40);
    if (!b || ebenen.length === 0) return () => clearTimeout(loesen);

    const stapel = ebenen.filter(e => !e.verlassend);
    const hinaus = ebenen.find(e => e.verlassend);
    const obenEl = flaechen.current.get(stapel[stapel.length - 1]?.ort.schluessel ?? '');
    const untenEl = stapel.length >= 2
      ? flaechen.current.get(stapel[stapel.length - 2].ort.schluessel) : null;

    // „Bewegung reduzieren" heißt nicht „keine Rückmeldung". Ein harter
    // Schnitt lässt die App kaputt wirken — Apple ersetzt Bewegung durch eine
    // Überblendung. Also: kein Schieben, aber ein Auf- und Abblenden.
    const ruhig = ruhigGewuenscht();
    const setzen = (el: HTMLElement, x: string, helligkeit: string, mitUebergang: boolean,
                    deckkraft?: number) => {
      el.style.transition = !mitUebergang ? 'none'
        : ruhig ? `opacity ${DAUER_RUHIG_MS}ms linear`
          : `transform ${DAUER_MS}ms ${KURVE}, filter ${DAUER_MS}ms ${KURVE}`;
      // Bei Ruhe bleibt die Seite stehen und wird ein-/ausgeblendet.
      el.style.transform = ruhig ? 'none' : `translateX(${x})`;
      el.style.filter = ruhig ? 'none' : helligkeit;
      if (deckkraft !== undefined) el.style.opacity = ruhig ? String(deckkraft) : '1';
    };
    const vorzeichen = richtungJetzt() === 'rtl' ? -1 : 1;

    laeuft.current = true;

    if (b.art === 'push' && obenEl) {
      setzen(obenEl, `${vorzeichen * 100}%`, 'none', false, 0);
      if (untenEl) setzen(untenEl, '0%', 'none', false, 1);
      // Ein erzwungenes Nachrechnen, sonst fasst der Browser Anfangs- und
      // Zielwert zu einem Sprung zusammen und es wird nichts animiert.
      void obenEl.offsetWidth;
      setzen(obenEl, '0%', 'none', true, 1);
      if (untenEl) setzen(untenEl, `${vorzeichen * -TIEFE_PROZENT}%`, 'brightness(.82)', true, 1);
    } else if (b.art === 'pop') {
      // Die darunterliegende kommt aus ihrer Tiefenlage zurück …
      if (obenEl) setzen(obenEl, '0%', 'none', true, 1);
      // … und die verlassende gleitet hinaus. Ihr aktueller Platz bleibt
      // stehen: beim Wischen ist das die Stelle, an der losgelassen wurde,
      // beim Tipp auf den Pfeil die Null. Von dort läuft der Übergang weiter.
      const hinausEl = hinaus && flaechen.current.get(hinaus.ort.schluessel);
      if (hinausEl) {
        void hinausEl.offsetWidth;
        setzen(hinausEl, `${vorzeichen * 100}%`, 'none', true, 0);
      }
    }
    return () => clearTimeout(loesen);
  }, [ebenen]);

  // --- Zurück-Taste von Android ---------------------------------------------
  // Auf der Wurzel beendet der erste Druck die App NICHT. Eine halb getippte
  // Rechnung wäre sonst durch einen Fehlgriff weg.
  const beendenBereit = useRef(false);
  const { t } = useSprache();
  const [hinweis, setHinweis] = useState<string | null>(null);

  useEffect(() => {
    if (!istNativ()) return;
    let abmelden: (() => void) | undefined;
    void (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const horcher = await App.addListener('backButton', () => {
          if (ebenen.length > 1) { navigate(-1); return; }
          if (beendenBereit.current) { void App.exitApp(); return; }
          beendenBereit.current = true;
          setHinweis(t('app.nochmalBeenden'));
          setTimeout(() => { beendenBereit.current = false; setHinweis(null); }, 2000);
        });
        abmelden = () => { void horcher.remove(); };
      } catch { /* ohne Plugin gibt es keine Zurück-Taste */ }
    })();
    return () => abmelden?.();
  }, [ebenen.length, navigate, t]);

  // --- Wischgeste -----------------------------------------------------------
  const zug = useRef<{ start: number; letzter: number; zeit: number; tempo: number } | null>(null);


  useEffect(() => {
    if (!schmal) return;
    const flaeche = document.getElementById('app-buehne');
    if (!flaeche) return;

    const breite = () => flaeche.clientWidth || window.innerWidth;

    // Eine gerade hinausgleitende Ebene gehört nicht mehr zum Stapel. Ohne
    // diese Unterscheidung griffe die Geste nach ihr statt nach der Seite,
    // die der Benutzer sieht.
    const stapel = () => ebenen.filter(e => !e.verlassend);

    const paare = () => {
      const st = stapel();
      const oben = flaechen.current.get(st[st.length - 1]?.ort.schluessel ?? '');
      const unten = flaechen.current.get(st[st.length - 2]?.ort.schluessel ?? '');
      return { oben, unten };
    };

    const runter = (e: PointerEvent) => {
      if (laeuft.current || stapel().length < 2) return;
      if (!imStartbereich(e.clientX, breite(), richtungJetzt())) return;
      // Liegt darunter etwas, das selbst seitwärts gescrollt ist, gehört die
      // Bewegung diesem Bereich. Betrifft heute Kasse und Chat.
      if (scrollerDavor(bahnVon(document.elementFromPoint(e.clientX, e.clientY)))) return;

      const { oben, unten } = paare();
      if (!oben) return;
      zug.current = { start: e.clientX, letzter: e.clientX, zeit: performance.now(), tempo: 0 };
      oben.style.transition = 'none';
      if (unten) unten.style.transition = 'none';
    };

    const ziehen = (e: PointerEvent) => {
      const z = zug.current;
      if (!z) return;
      const jetzt = performance.now();
      const dt = jetzt - z.zeit;
      if (dt > 0) z.tempo = Math.abs(e.clientX - z.letzter) / dt;
      z.letzter = e.clientX;
      z.zeit = jetzt;

      const weg = wegAus(e.clientX, z.start, richtungJetzt());
      const anteil = Math.min(1, weg / breite());
      const { oben, unten } = paare();
      const vorzeichen = richtungJetzt() === 'rtl' ? -1 : 1;
      if (oben) oben.style.transform = `translateX(${vorzeichen * weg}px)`;
      if (unten) {
        unten.style.transform = `translateX(${vorzeichen * (-TIEFE_PROZENT + TIEFE_PROZENT * anteil)}%)`;
        unten.style.filter = `brightness(${(0.82 + 0.18 * anteil).toFixed(3)})`;
      }
    };

    const hoch = (e: PointerEvent) => {
      const z = zug.current;
      if (!z) return;
      zug.current = null;
      const weg = wegAus(e.clientX, z.start, richtungJetzt());
      const { oben, unten } = paare();
      const uebergang = ruhigGewuenscht() ? 'none'
        : `transform ${DAUER_MS}ms ${KURVE}, filter ${DAUER_MS}ms ${KURVE}`;
      if (oben) oben.style.transition = uebergang;
      if (unten) unten.style.transition = uebergang;

      if (loestAus(weg, breite(), z.tempo)) {
        laeuft.current = true;
        void tippen('leicht');
        navigate(-1);
      } else {
        // Zurückfedern: alles wieder an seinen Platz.
        const vorzeichen = richtungJetzt() === 'rtl' ? -1 : 1;
        if (oben) oben.style.transform = 'translateX(0)';
        if (unten) {
          unten.style.transform = `translateX(${vorzeichen * -TIEFE_PROZENT}%)`;
          unten.style.filter = 'brightness(.82)';
        }
      }
    };

    flaeche.addEventListener('pointerdown', runter);
    flaeche.addEventListener('pointermove', ziehen);
    flaeche.addEventListener('pointerup', hoch);
    flaeche.addEventListener('pointercancel', hoch);
    return () => {
      flaeche.removeEventListener('pointerdown', runter);
      flaeche.removeEventListener('pointermove', ziehen);
      flaeche.removeEventListener('pointerup', hoch);
      flaeche.removeEventListener('pointercancel', hoch);
    };
  }, [schmal, ebenen, navigate]);

  return (
    <div id="app-buehne" data-ebenen={ebenen.length} data-art={art}
         className="relative min-h-0 flex-1 overflow-hidden">
      {/* Der Streifen, in dem die Geste beginnen darf, ist hier nur
          dokumentiert — gemessen wird in der Gestenlogik. Ohne ihn wüsste
          niemand beim Lesen, wie breit „am Rand" ist. */}
      <span hidden data-randbreite={RANDBREITE} />
      {hinweis && (
        <p className="pointer-events-none absolute inset-x-0 z-50 mx-auto w-fit rounded-full bg-slate-900/90 px-4 py-2 text-sm text-white"
           style={{ bottom: 'calc(1rem + var(--sicher-unten))' }} role="status">
          {hinweis}
        </p>
      )}
      {ebenen.map((e, i) => {
        // Die oberste Ebene des STAPELS — nicht die letzte im Feld. Gleitet
        // gerade eine Ebene hinaus, steht sie hinter dem Stapel im Feld und
        // wäre sonst fälschlich die oberste.
        const obersteStapel = ebenen.reduce(
          (letzte, x, n) => (x.verlassend ? letzte : n), -1);
        const zurueck = !e.verlassend && i !== obersteStapel;
        return (
        <div
          key={e.ort.schluessel}
          ref={el => { if (el) flaechen.current.set(e.ort.schluessel, el); else flaechen.current.delete(e.ort.schluessel); }}
          // touch-action: der Browser darf senkrecht scrollen, die waagerechte
          // Bewegung gehört uns — sonst nimmt er die Geste an sich.
          style={{
            // Verlassende Ebenen bekommen ihre Verschiebung von der Animation
            // und dürfen hier nicht überschrieben werden.
            transform: e.verlassend ? undefined
              : zurueck ? `translateX(-${TIEFE_PROZENT}%)` : undefined,
            filter: e.verlassend ? undefined : zurueck ? 'brightness(.82)' : undefined,
            // Eine Ebene, die hinausgleitet, nimmt keine Tipps mehr an —
            // sonst trifft ein Finger während der Bewegung noch ihre Knöpfe.
            pointerEvents: e.verlassend ? 'none' : undefined,
            zIndex: e.verlassend ? ebenen.length + 1 : i,
          }}
          className={`absolute inset-0 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-950 ${
            i > 0 || e.verlassend ? 'shadow-[-8px_0_24px_rgba(0,0,0,0.18)]' : ''}`}
          aria-hidden={zurueck || e.verlassend}
          // inert hält auch den Tastaturfokus draußen. Mit aria-hidden allein
          // landet die Eingabemarke auf unsichtbaren Knöpfen — am iPad mit
          // Tastatur ein echter Fall, denn das liegt unter 1024 px.
          inert={zurueck || e.verlassend}
        >
          {rahmen ? rahmen(e.inhalt, i, e.ort.pfad) : e.inhalt}
        </div>
        );
      })}
    </div>
  );
}
